/*>
* @license
* Copyright (c) 2014-2022 ABILITY Network, Inc., an INOVALON, Inc. company. All Rights Reserved.
* 
* This computer program is CONFIDENTIAL and a TRADE SECRET of ABILITY Network, Inc.
* The receipt or possession of this program does not convey any rights to use,
* reproduce or disclose its contents in whole or in part, without the specific
* written consent of ABILITY Network, Inc. Any use, reproduction or disclosure of
* this program without the express written consent of ABILITY Network, Inc. is a
* violation of the copyright laws and may subject you to criminal prosecution.
<*/
import React from 'react'
import './SearchResults.scss'
import { RouteComponentProps, Route } from 'react-router'
import { ISearchResultsStateManager, ISection } from '../../services/SearchResultsStateManager/contracts'
import { observer } from 'mobx-react'
import { container } from '../../configuration/ServiceLocator'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { Input, FormFeedback, Breadcrumb, BreadcrumbItem } from 'reactstrap'
import queryString from 'query-string'
import ReactGA from 'react-ga4'
import { UserEventLogger } from '../../utils/UserEventLogger'

export interface ISearchResultsProps extends RouteComponentProps<any> {
	searchResultsStateManager: ISearchResultsStateManager
}

export interface ISearchResultsState {
	first: number
	skip: number
	resultsLoaded: boolean
}

@observer
export class SearchResults extends React.Component<ISearchResultsProps, ISearchResultsState> {
	searchTerm: string
	currentPage: number // This is for Pagination info on page load
	noResultTerm: string // The Search text for which won't have any results
	pageIndex: number
	endofsearchResults: boolean
	constructor(props: Readonly<ISearchResultsProps>) {
		super(props)
		this.state = {
			first: 5, // Default no of records per page
			skip: 0, // Skipping 0 records first
			resultsLoaded: false,
		}
		this.currentPage = 1
		this.searchTerm = ''
		this.noResultTerm = ''
		this.handleScroll = this.handleScroll.bind(this)
		this.pageIndex = 0
		this.endofsearchResults = false

		this.onSearchEnter = this.onSearchEnter.bind(this)
		this.SearchResults = this.SearchResults.bind(this)

		window.addEventListener('scroll', this.handleScroll, { passive: true })
	}

	componentDidMount() {
		const searchurl = this.props.location.search
		this.searchTerm = this.noResultTerm = queryString.parse(searchurl)['searchTerm'] as string
		this.props.searchResultsStateManager.setSearchTerm(this.searchTerm)
		this.getSearchResults(this.searchTerm, this.state.first, this.state.skip, 'didmount')
	}

	componentDidUpdate(prevSearchTerm: any) {
		if (this.props.location.search != prevSearchTerm.location.search) {
			const searchurl = this.props.location.search
			this.searchTerm = this.noResultTerm = queryString.parse(searchurl)['searchTerm'] as string
			this.props.searchResultsStateManager.setSearchTerm(this.searchTerm)
			this.getSearchResults(this.searchTerm, this.state.first, this.state.skip, 'didupdate')
		}
	}

	handleScroll(event: any) {
		const { pageYOffset } = window
		if (pageYOffset > 50) {
			this.pageIndex = this.pageIndex + 1
			this.getSearchResults(this.props.searchResultsStateManager.searchTerm.trim(), this.state.first, this.state.first * this.pageIndex, 'scroll')
		}
	}
	/**Escape Special Characters for Sesrch string*/
	escapeSpclCharsSearchTerm(searchTerm: string): string {
		const format = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/
		if (format.test(searchTerm)) {
			return searchTerm.replace(/[\"\\s]/g, '\\$&')
		}
		return searchTerm
	}

	/**Common method to get the search results */
	getSearchResults(searchTerm: string, first: number, skip: number, searchType: string) {
		this.props.searchResultsStateManager
			.getSearchResults(searchTerm, first, skip, searchType)
			.then(response => this.setState({ resultsLoaded: true }))
			.catch(error => console.error(error))
	}

	/**On Search button click*/
	SearchResults(event: any) {
		event.preventDefault()
		this.pageIndex = 0
		// halt if validation fails
		if (this.props.searchResultsStateManager.searchTerm.trim() && this.props.searchResultsStateManager.searchTerm.trim() != '') {
			this.getSearchResults(this.props.searchResultsStateManager.searchTerm.trim(), this.state.first, this.state.skip, 'click')
		}
		this.props.history.push('/pages/SearchResults?searchTerm=' + encodeURIComponent(this.props.searchResultsStateManager.searchTerm.trim()))
		this.handleUserClickEvent(event, 'SupportSite - SearchResults Page Search', 'Search - ' + this.props.searchResultsStateManager.searchTerm.trim())
	}

	/**On Enter Key Press  */
	onSearchEnter(event: any) {
		if (event.key === 'Enter') {
			event.preventDefault()
			event.stopPropagation()
			this.noResultTerm = event.target.value.trim()
			this.pageIndex = 0
			this.getSearchResults(event.target.value.trim(), this.state.first, this.state.skip, 'keypress')
			this.props.history.push('/pages/SearchResults?searchTerm=' + encodeURIComponent(event.target.value.trim()))
			this.handleUserClickEvent(event, 'SupportSite - SearchResults Page Search', 'Search - ' + event.target.value.trim())
			return false
		}
	}

	handleBreadCrumbClick(event: any) {
		this.handleUserClickEvent(event, 'SupportSite - SearchResults Page BreadCrumb Click', '')
	}

	handleArticleLinkClick(event: any) {
		this.handleUserClickEvent(event, 'SupportSite - SearchResults Page Article Link Click', '')
	}

	handleProductLinkClick(event: any) {
		this.handleUserClickEvent(event, 'SupportSite - SearchResults Page Product Link Click', '')
	}

	handleSectionLinkClick(event: any) {
		this.handleUserClickEvent(event, 'SupportSite - SearchResults Page Section Link Click', '')
	}

	handleUserClickEvent(event: any, eventType: any, eventText: any) {
		UserEventLogger.handleUserClickEvent(event, eventType, eventText)
	}

	/**Search Result List Template */
	getSearchResultTemplate(): any {
		return this.props.searchResultsStateManager.results.items.map((val, index) => {
			return (
				<div key={index} className="searchItemsList">
					<div className="search-result">
						<h4>
							<a href={'./help/pages/' + val.articlePath} onClick={e => this.handleArticleLinkClick(e)}>
								{val.articleName}
							</a>
						</h4>
						<p>{val.articleDescription}</p>
					</div>
					<div className="search-result-info">
						<span>
							<a href={'./pages/product/' + val.productCode + '/' + val.productName} onClick={e => this.handleProductLinkClick(e)}>
								{val.productName}
							</a>
						</span>
						<span>{' | '}</span>
						<span>
							{val.sectionList.length > 0
								? val.sectionList.map((section: ISection, sectionIndex: number) => {
									const appendComma = ', '
									return sectionIndex > 0 ? (
										<span key={sectionIndex}>
											{appendComma}{' '}
											<a href={'./pages/product/' + val.productCode + '/' + val.productName + '/sectionid/' + section.sectionId  + '/sectionName/' + section.sectionName} key={sectionIndex} onClick={e => this.handleSectionLinkClick(e)}>
												{section.sectionName}
											</a>
										</span>
									) : (
										<a href={'./pages/product/' + val.productCode + '/' + val.productName + '/sectionid/' + section.sectionId  + '/sectionName/' + section.sectionName} key={sectionIndex} onClick={e => this.handleSectionLinkClick(e)}>
											{section.sectionName}
										</a>
									)
								  })
								: ''}
						</span>
						<span>{val.sectionList.length > 0 ? ' | ' : ''}</span>
						<span>{val.articlePublishDate}</span>
					</div>
					<div className="hr-line-dashed"></div>
				</div>
			)
		})
	}

	render() {
		/** To show results (or) empty result and for pagination info*/
		let resultTemplate
		let endTemplate

		if (this.state.resultsLoaded) {
			if (this.props.searchResultsStateManager.results.items.length > 0) {
				resultTemplate = this.getSearchResultTemplate()
			} else {
				resultTemplate = (
					<h3>
						No results found for <b>"{this.noResultTerm}"</b>
					</h3>
				)
			}
		}

		if (this.props.searchResultsStateManager.results.items.length > 0) {
			if (this.props.searchResultsStateManager.results.total < this.state.first) {
				endTemplate = (
					<h4>
						<b>--- End of search results ---</b>
					</h4>
				)
			}
		}

		return (
			<div className="container bootstrap snippets bootdey">
				<div>
					<Breadcrumb>
						<BreadcrumbItem>
							<a href="/supportsite/pages/landingpage" onClick={e => this.handleBreadCrumbClick(e)}>
								Support Home
							</a>
						</BreadcrumbItem>
						<BreadcrumbItem active> Search Results</BreadcrumbItem>
					</Breadcrumb>
				</div>
				<div className="row">
					<div className="col-lg-12">
						<div className="ibox float-e-margins">
							<div className="ibox-content">
								<div className="search-form">
									<form>
										<div className="input-group">
											<Input type="text" placeholder="Search For.." name="searchTerm" className="form-control input-lg searchResult-search-no-border" onKeyDown={e => this.onSearchEnter(e)} value={this.props.searchResultsStateManager.searchTerm} onChange={e => this.props.searchResultsStateManager.setSearchTerm(e.target.value)} />
											<div className="input-group-append searchReult-search-div">
												<button type="button" className="btn btn-secondary search-btn" onClick={e => this.SearchResults(e)}>
													<FontAwesomeIcon icon={faSearch} />
												</button>
											</div>
										</div>
									</form>
								</div>
								{this.state.resultsLoaded ? <div className="divSearchReults">{resultTemplate}</div> : ''}
								{endTemplate}
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}
}

export const SearchResultsConnected = () => {
	const store = container.get<ISearchResultsStateManager>('ISearchResultsStateManager')
	
	return (
		<Route 
		exact path="/pages/SearchResults" 
		render={props => { 
			ReactGA.send({ hitType: "pageview", page: "props.location.pathname" }); 
			return <SearchResults {...props} searchResultsStateManager={store} />} }/>
	)
}
